import React, { useState } from 'react'

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { Alert, Box, Paper, TextField, Typography } from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { useSnackbar } from 'notistack'

import { lerMensagemDeErro, montarDefinicaoPorFrase } from '../service/reportService'
import { aplicarDefinicaoNoBuilder } from './aplicarDefinicaoNoBuilder'

const EXEMPLO = 'Ex.: as vendas por canal do mês passado, da maior para a menor'
// Uma linha de ajuda: a lista do que ela monta já está no campo "O que você quer ver".
const AJUDA = 'Escreva com suas palavras e a Sisa preenche as opções abaixo. Você confere tudo antes de gerar.'

export default function AiPrompt({ datasets, onAplicar }) {
    const { enqueueSnackbar } = useSnackbar()
    const [frase, setFrase] = useState('')
    const [carregando, setCarregando] = useState(false)
    // Aviso da IA fica na tela até o usuário fechar: snackbar de 5s some antes de ler.
    const [aviso, setAviso] = useState('')

    async function handleMontar() {
        if (!frase.trim()) {
            return
        }

        setCarregando(true)
        setAviso('')

        try {
            const resposta = await montarDefinicaoPorFrase(frase.trim())
            const estado = aplicarDefinicaoNoBuilder(resposta?.definicao, datasets)

            // Definição fora do catálogo: o montador continua como estava.
            if (!estado) {
                enqueueSnackbar(
                    'Não consegui montar esse relatório. Escolha as opções abaixo na mão.',
                    { variant: 'warning' },
                )
                return
            }

            onAplicar(estado)
            enqueueSnackbar('Pronto! Confira as opções abaixo e clique em Gerar relatório.', {
                variant: 'success',
            })

            // A IA avisa quando teve que escolher (ex.: a frase pedia dois relatórios).
            const recado = typeof resposta?.aviso === 'string' ? resposta.aviso.trim() : ''
            if (recado) {
                setAviso(recado)
            }
        } catch (err) {
            console.error('Erro ao montar o relatório com IA:', err)
            enqueueSnackbar(
                await lerMensagemDeErro(
                    err,
                    'Não consegui montar o relatório agora. Escolha as opções abaixo na mão.',
                ),
                { variant: 'error' },
            )
        } finally {
            setCarregando(false)
        }
    }

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
            <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.5 }}>
                <AutoAwesomeIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1">
                    Descreva o relatório que você quer
                </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" align="left" sx={{ mb: 1.5 }}>
                {AJUDA}
            </Typography>

            <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
                <TextField
                    fullWidth
                    label="O que você quer no relatório?"
                    placeholder={EXEMPLO}
                    value={frase}
                    disabled={carregando}
                    onChange={event => setFrase(event.target.value)}
                    sx={{ flex: '1 1 320px' }}
                />

                <LoadingButton
                    variant="contained"
                    startIcon={<AutoAwesomeIcon />}
                    loading={carregando}
                    loadingIndicator="Montando..."
                    disabled={!frase.trim()}
                    onClick={handleMontar}
                >
                    Montar com IA
                </LoadingButton>
            </Box>

            {!!aviso && (
                <Alert severity="info" sx={{ mt: 2 }} onClose={() => setAviso('')}>
                    {aviso}
                </Alert>
            )}
        </Paper>
    )
}
